import { useMemo } from "react"
import { useParams } from "react-router-dom"
import HomeLayout from "modules/home-layout"
import PreferenceContext from "hooks/preferences"
import { useAccountId } from "resources/account"
import Loading from "components/loading"
import { AiContext } from "connected-components/ai-teammate/context/ai-context"
import { useClearDashboardPreferences } from "./hooks/use-clear-dashboard-preferences"
import { DashboardIdProvider, useDashboardId, DEFAULT_DASHBOARD_ID } from "./context/dashboard-id"
import { getDashboardPreferenceKey, getDashboardPreferenceFallbackKey } from "./helpers/preference-keys"
import { useDashboardPreferencesSync } from "./hooks/use-dashboard-preferences-sync"
import { useModelId } from "./hooks/use-model-id"
import { useModelSync } from "./hooks/use-model-sync"
import { useAppSelector } from "hooks/redux"
import { DEFAULT_CORE_STATE } from "./store/core-slice"
import TradeSyncEffect from "./components/trade-sync-effect"

function DashboardWithContext({ children }) {
  const dashboardId = useDashboardId()
  const isStoreLoaded = useDashboardPreferencesSync()
  const modelId = useModelId()

  const core = useAppSelector((s) => s.dashboard.core[dashboardId] ?? DEFAULT_CORE_STATE)

  // One model-sync per dashboard — keep global home layout in sync with modelId.
  useModelSync(modelId)

  const aiContextValue = useMemo(() => ({
    modelId,
    instrument: core.instrument,
    base: core.base,
    startDate: core.startDate,
    endDate: core.endDate,
    isPreferencesLoaded: isStoreLoaded,
    canPinArtifacts: true,
  }), [
    modelId,
    core.instrument,
    core.base,
    core.startDate,
    core.endDate,
    isStoreLoaded,
  ])

  if (!isStoreLoaded) {
    return <Loading />
  }

  return (
    <AiContext.Provider value={aiContextValue}>
      <TradeSyncEffect />
      {children}
    </AiContext.Provider>
  )
}

export default function DashboardHomeLayout() {
  const accountId = useAccountId()
  const { dashboardId: routeDashboardId } = useParams()
  // || not ??: an empty path segment must also fall back, or "" becomes a distinct dashboard.
  const dashboardId = routeDashboardId || DEFAULT_DASHBOARD_ID

  const { isClearing: isPreferencesClearing } = useClearDashboardPreferences(dashboardId)

  const preferencesProps = useMemo(() => ({
    accountId,
    key: getDashboardPreferenceKey(dashboardId),
    fallbackKey: getDashboardPreferenceFallbackKey(dashboardId),
    type: "dashboard",
  }), [accountId, dashboardId])

  if (isPreferencesClearing) {
    return <Loading />
  }

  // key forces a remount when the route param changes: React Router keeps the element mounted
  // across param-only navigations, but the preference sync hooks require a stable dashboardId
  // for their lifetime (one-shot load refs in usePreferences).
  return (
    <PreferenceContext.Provider key={dashboardId} value={preferencesProps}>
      <DashboardIdProvider value={dashboardId}>
        <DashboardWithContext>
          <HomeLayout />
        </DashboardWithContext>
      </DashboardIdProvider>
    </PreferenceContext.Provider>
  )
}
